// Reglas del paso Referidos: validación del formulario, lo que se manda al silo al guardar o corregir
// un referido y la traducción de sus errores al campo de la pantalla.
import { ApiError } from '@/api/errors';
import type { SessionReferralInput } from '@/api/services/retail-wizard/contract';
import { PHONE_MX_DIGITS } from '@/lib/phoneMx';
import {
    capitalizeFirst,
    CUSTOMER_FALLBACK_NAME,
    ofCustomer,
} from '@/shared/lib/format/customerMention';

/** Codes que dicen que la sesión cambió por fuera: se recarga para que el router la explique. */
export const STALE_SESSION_CODES = new Set(['INVALID_SESSION_STATE', 'SESSION_CANCELLED']);

/** Lo capturado en el formulario; `phone` son solo dígitos (lo entrega así el `PhoneInput`). */
export interface ReferralDraft {
    name: string;
    phone: string;
    email: string;
}

export type ReferralField = keyof ReferralDraft;

export type DraftErrors = Partial<Record<ReferralField, string>>;

export interface ReferralContext {
    customerPhone: string;
    customerName: string;
    /** Teléfonos de los otros referidos del cliente (sin el que se está editando). */
    knownPhones: readonly string[];
}

export const REFERRALS_COPY = {
    eyebrow: 'Referidos',
    title: '¿A quién le recomienda SureKeep?',
    sub: (name: string) => `Captura a las personas que ${name} recomienda. Este paso es opcional.`,
    existingTitle: (name: string) => `Referidos ${ofCustomer(name)}`,
    existingEmpty: (name: string) => capitalizeFirst(`${name} todavía no tiene referidos.`),
    existingError: 'No pudimos cargar sus referidos',
    savedPill: 'Guardado',
    edit: 'Editar',
    editLabel: (name: string) => `Editar a ${name}`,
    addTitle: 'Agregar referido',
    editTitle: 'Editar referido',
    addHint:
        'Nombre y teléfono son obligatorios; el correo es opcional. Guarda cada referido antes de agregar el siguiente.',
    editHint: 'Corrige nombre, teléfono o correo y guarda los cambios.',
    nameLabel: 'Nombre completo',
    namePlaceholder: 'Ej. Rosa Hernández Vega',
    phoneLabel: 'Teléfono',
    phonePlaceholder: '10 dígitos',
    emailLabel: 'Correo (opcional)',
    emailPlaceholder: 'nombre@correo.com',
    saveReferral: 'Guardar referido',
    saveChanges: 'Guardar cambios',
    cancelEdit: 'Cancelar',
    saved: 'Referido guardado',
    updated: 'Referido actualizado',
    noChanges: 'No cambiaste ningún dato.',
    pendingTitle: 'Tienes un referido sin guardar',
    pendingText: 'Si continúas, ese referido no se guarda.',
    keepCapturing: 'Seguir capturando',
    continueAnyway: 'Continuar sin guardar',
    continue: 'Continuar',
    saveFailed: 'No pudimos guardar el referido. Intenta de nuevo.',
    notFound: 'Este referido ya no está. Recargamos la lista; vuelve a intentarlo.',
    skipFailed: 'No pudimos continuar. Intenta de nuevo.',
} as const;

const MSG = {
    nameRequired: 'Ingresa el nombre.',
    nameInvalid: 'Revisa el nombre.',
    phoneRequired: 'Ingresa el teléfono.',
    phoneLength: 'El teléfono debe tener 10 dígitos.',
    phoneInvalid: 'Revisa el teléfono; debe tener 10 dígitos.',
    emailInvalid: 'Revisa el correo; le falta algo.',
    isCustomer: (name: string) => `Es el teléfono ${ofCustomer(name)}; ingresa el de su referido.`,
    known: (name: string) => `Ese teléfono ya es referido ${ofCustomer(name)}.`,
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function blankDraft(): ReferralDraft {
    return { name: '', phone: '', email: '' };
}

export function isBlankDraft(draft: ReferralDraft): boolean {
    return !draft.name.trim() && !draft.phone.trim() && !draft.email.trim();
}

/** Errores del formulario; vacío si se puede guardar. */
export function validateDraft(draft: ReferralDraft, ctx: ReferralContext): DraftErrors {
    const errors: DraftErrors = {};
    if (!draft.name.trim()) errors.name = MSG.nameRequired;
    const phoneError = validatePhone(draft.phone, ctx);
    if (phoneError) errors.phone = phoneError;
    const email = draft.email.trim();
    if (email && !EMAIL_PATTERN.test(email)) errors.email = MSG.emailInvalid;
    return errors;
}

function validatePhone(phone: string, ctx: ReferralContext): string | undefined {
    if (!phone) return MSG.phoneRequired;
    if (phone.length !== PHONE_MX_DIGITS) return MSG.phoneLength;
    if (phone === ctx.customerPhone) return MSG.isCustomer(ctx.customerName);
    if (ctx.knownPhones.includes(phone)) return MSG.known(ctx.customerName);
    return undefined;
}

export function hasErrors(errors: DraftErrors): boolean {
    return Object.keys(errors).length > 0;
}

/** El referido que se manda al silo, recortado y sin correo vacío. */
export function toReferralInput(draft: ReferralDraft): SessionReferralInput {
    const email = draft.email.trim();
    return { name: draft.name.trim(), phone: draft.phone, ...(email && { email }) };
}

export interface ServerDraftError {
    field: ReferralField;
    message: string;
}

const FIELD_MESSAGE: Record<ReferralField, string> = {
    name: MSG.nameInvalid,
    phone: MSG.phoneInvalid,
    email: MSG.emailInvalid,
};

/** El error del silo pegado al campo del formulario; `null` si no señala un campo. */
export function serverDraftError(error: unknown, customerName: string): ServerDraftError | null {
    if (!(error instanceof ApiError)) return null;
    const details = (error.data ?? {}) as { field?: unknown };
    switch (error.code) {
        case 'INVALID_REFERRAL_DATA': {
            const field = details.field;
            if (field !== 'name' && field !== 'phone' && field !== 'email') return null;
            return { field, message: FIELD_MESSAGE[field] };
        }
        case 'REFERRAL_IS_CUSTOMER':
            return { field: 'phone', message: MSG.isCustomer(customerName) };
        case 'DUPLICATE_REFERRAL_PHONE':
            return { field: 'phone', message: MSG.known(customerName) };
        default:
            return null;
    }
}

/** Orden alfabético por nombre, sin distinguir mayúsculas ni acentos. */
export function sortReferrals<T extends { name?: string }>(list: readonly T[]): T[] {
    return [...list].sort((a, b) =>
        (a.name ?? '').localeCompare(b.name ?? '', 'es', { sensitivity: 'base' }),
    );
}

/** Cómo se nombra al cliente en la pantalla: su nombre de pila o «el cliente». */
export function customerFirstName(session: {
    firstName?: string;
    kyc?: { verified?: { fullName: string } | null };
}): string {
    const fromKyc = session.kyc?.verified?.fullName.trim().split(/\s+/)[0];
    return session.firstName?.trim() || fromKyc || CUSTOMER_FALLBACK_NAME;
}
